import { Modal, SubmitButton, Table } from "@xola/ui-kit";
import { DemographicIcon, FormField, useError } from "common";
import { capitalize, sortBy } from "lodash";
import React from "react";
import { DemographicPrice } from "types";
import { array, number, object } from "yup";
import { isDefaultDemographic } from "../../../models/DemographicUnit";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { CurrencyInputField } from "../../Field/CurrencyInputField";

interface EditFlatPricingModalProps {
    isOpen: boolean;
    isEquipment?: boolean;
    demographicPrices: DemographicPrice[];
    onClose: () => void;
    onSubmit: (demographicPrices: DemographicPrice[]) => void;
}

export const EditFlatPricingModal = ({
    isOpen,
    isEquipment,
    demographicPrices,
    onClose,
    onSubmit,
}: EditFlatPricingModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent
                isEquipment={isEquipment}
                demographicPrices={demographicPrices}
                onClose={onClose}
                onSubmit={onSubmit}
            />
        </Modal>
    );
};

const validationSchema = array().of(
    object().shape({
        price: number().min(0).typeError("must be a number").required(),
    }),
);

type ModalContentProps = Omit<EditFlatPricingModalProps, "isOpen">;

const ModalContent = ({ isEquipment, demographicPrices, onClose, onSubmit }: ModalContentProps) => {
    const sortedDemographicPrices = sortBy(demographicPrices, "catalogItem.sequence");

    return (
        <XolaFormik initialValues={sortedDemographicPrices} validationSchema={validationSchema} onSubmit={onSubmit}>
            {({ handleSubmit, values }) => (
                <>
                    <Modal.Header>Base Prices</Modal.Header>

                    <Modal.Body>
                        <div className="space-y-2">
                            <div className="text-sm font-bold">
                                <span>Terms</span>
                            </div>

                            <Table>
                                <Table.Head>
                                    <Table.Row>
                                        <Table.Header className="!text-sm">
                                            {isEquipment ? "Equipment" : "Demographics"}
                                        </Table.Header>
                                        <Table.Header className="!text-sm">Price</Table.Header>
                                    </Table.Row>
                                </Table.Head>

                                <Table.Body isStriped={false}>
                                    {values.map((demographicPrice, index) => (
                                        <DemographicPriceItem
                                            key={demographicPrice.template.id}
                                            isEquipment={isEquipment}
                                            demographicPrice={demographicPrice}
                                            index={index}
                                        />
                                    ))}
                                </Table.Body>
                            </Table>
                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                        <SubmitButton type="submit" onClick={handleSubmit}>
                            Save
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

interface DemographicPriceItemProps {
    isEquipment?: boolean;
    demographicPrice: DemographicPrice;
    index: number;
}

const DemographicPriceItem = ({ isEquipment, demographicPrice, index }: DemographicPriceItemProps) => {
    const error = useError(`${index}.price`);

    return (
        <Table.Row key={demographicPrice.template.id} className="justify-between">
            <Table.Cell className="w-3/5">
                <NoTranslate
                    if={!isDefaultDemographic(demographicPrice.catalogItem!.name)}
                    className="whitespace-normal break-all"
                >
                    {!isEquipment && <DemographicIcon className="mr-1" name={demographicPrice.catalogItem!.name} />}
                    {demographicPrice.catalogItem!.name}
                </NoTranslate>
            </Table.Cell>

            <Table.Cell className="w-2/5">
                <FormField label={null} name={`${index}.price`}>
                    <CurrencyInputField isRequired isError={!!error} />
                    {error ? (
                        <span className="text-sm font-bold text-danger">{capitalize(error.split(".")[1])}</span>
                    ) : null}
                </FormField>
            </Table.Cell>
        </Table.Row>
    );
};
